import { downloadMergedPdf, fetchMergePreview } from "@rpforms/shared/build/actions";
import { PrimaryButton } from "@rpforms/shared/build/components/Button";
import { arrayMove } from "@rpforms/shared/build/utils";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { toast } from "react-toastify";
import styled from "styled-components";

const MergeList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const MergeRow = styled.li<{ unsupported?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 20px;
    border-bottom: 1px solid #eee;
    background: #fff;
    opacity: ${(props) => (props.unsupported ? 0.5 : 1)};

    span {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    button {
        border: 0;
        background: none;
        cursor: pointer;
    }
`;

const Handle = styled.i`
    cursor: grab;
`;

const DragHandle = SortableHandle(() => <Handle className="fa fa-bars" />);

const SortableRow = SortableElement(({ item, onRemove }: any) => (
    <MergeRow style={{ zIndex: 2000 }}>
        <DragHandle />
        <i className={item.kind === "image" ? "fa fa-file-image-o" : "fa fa-file-pdf-o"} />
        <span title={item.name}>{item.name}</span>
        <button type="button" title="Entfernen" onClick={() => onRemove(item.upload_id)}>
            <i className="fa fa-times" />
        </button>
    </MergeRow>
));

const SortableList: any = SortableContainer(({ items, onRemove }: any) => (
    <MergeList>
        {items.map((item, index) => (
            <SortableRow key={item.upload_id} index={index} item={item} onRemove={onRemove} />
        ))}
    </MergeList>
));

export const MergePdfModal = ({ folderIds, onHide }) => {
    const [items, setItems] = useState([]);
    const [unsupported, setUnsupported] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchMergePreview(folderIds)
            .then((preview) => {
                setItems(preview.filter((item) => item.supported));
                setUnsupported(preview.filter((item) => !item.supported));
            })
            .catch((e) => {
                toast.error(e.message);
                onHide();
            })
            .finally(() => setLoading(false));
    }, []);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        setItems((current) => arrayMove(current, oldIndex, newIndex));
    };

    const remove = (uploadId) => {
        setItems((current) => current.filter((item) => item.upload_id !== uploadId));
    };

    const create = async () => {
        setCreating(true);
        try {
            const skipped = await downloadMergedPdf(
                folderIds,
                items.map((item) => item.upload_id)
            );
            if (skipped > 0) {
                toast.warn(`${skipped} Datei(en) konnten nicht übernommen werden`);
            }
            onHide();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setCreating(false);
        }
    };

    return (
        <Modal show={true} onHide={onHide} size={"lg"} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>PDF zusammenführen</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                {loading && <div style={{ padding: 20 }}>lädt …</div>}
                {!loading && (
                    <>
                        <div style={{ padding: "10px 20px" }}>
                            Reihenfolge per Ziehen am <i className="fa fa-bars" /> festlegen.
                        </div>
                        <SortableList
                            items={items}
                            onRemove={remove}
                            onSortEnd={onSortEnd}
                            useDragHandle
                            lockAxis="y"
                        />
                        {unsupported.length > 0 && (
                            <div style={{ padding: "10px 20px" }}>
                                <small>
                                    Nicht unterstützt und ausgelassen (nur PDF, JPG, PNG):
                                </small>
                                {unsupported.map((item) => (
                                    <MergeRow key={item.upload_id} unsupported>
                                        <span title={item.name}>{item.name}</span>
                                    </MergeRow>
                                ))}
                            </div>
                        )}
                        <div style={{ padding: 20 }}>
                            <PrimaryButton
                                onClick={create}
                                disabled={creating || items.length === 0}
                            >
                                {creating
                                    ? "PDF wird erstellt …"
                                    : `PDF erzeugen (${items.length})`}
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};
